import React, { useEffect, useRef, useState } from 'react';
import { CheckoutButton } from './CheckoutButton';
import { LIGHTBOX_EVENT } from './Lightbox/Lightbox';
import { PRICE } from '../data/checkout';
import { PRODUCT } from '../data/welding';

/** Elements that make the bar redundant while they are on screen. */
const HIDE_WHEN_VISIBLE = ['offer-cta', 'final-cta', 'site-footer'];
const inView = (id: string) => {
  const el = document.getElementById(id);
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  return rect.top < window.innerHeight && rect.bottom > 0;
};

/**
 * Mobile-only purchase bar. Dormant until the visitor has actually seen the
 * offer price; afterwards it shows unless another buy button, the footer or
 * the page viewer is on screen. Uses the same checkout as the offer.
 */
export function StickyBuyBar() {
  const [visible, setVisible] = useState(false);
  const seenOffer = useRef(false);
  const lightboxOpen = useRef(false);
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      if (!seenOffer.current && inView('offer-price')) seenOffer.current = true;
      setVisible(seenOffer.current && !lightboxOpen.current && !HIDE_WHEN_VISIBLE.some(inView));
    };
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };
    const onLightbox = (event: Event) => {
      lightboxOpen.current = Boolean((event as CustomEvent<boolean>).detail);
      schedule();
    };
    update();
    window.addEventListener('scroll', schedule, {
      passive: true
    });
    window.addEventListener('resize', schedule);
    window.addEventListener(LIGHTBOX_EVENT, onLightbox);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener(LIGHTBOX_EVENT, onLightbox);
    };
  }, []);
  return <div aria-hidden={!visible} className={`fixed inset-x-3 z-40 transition-[transform,opacity] duration-[250ms] md:hidden ${visible ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-[calc(100%+24px)] opacity-0'}`} style={{
    bottom: 'calc(12px + env(safe-area-inset-bottom))',
    transitionTimingFunction: 'cubic-bezier(0.23, 1, 0.32, 1)'
  }} data-id="element-291" data-mp="1bs0k4m61sqar">
      <div className="flex items-center justify-between gap-3 rounded-[14px] bg-surface py-2 pl-4 pr-2 shadow-[0_20px_40px_-12px_rgba(0,0,0,0.7)] ring-1 ring-arc/30" data-id="element-292" data-mp="2jzem9nag3kna">
        <p className="min-w-0 leading-tight" data-id="element-293" data-mp="2t2frllrhl7pw">
          <span className="wordmark block truncate text-[0.82rem] text-white" data-id="element-294" data-mp="2t1e6eogrb4ug">
            {PRODUCT.wordmarkA} {PRODUCT.wordmarkB}
            <sup className="font-sans text-[0.5em]" data-id="element-295" data-mp="32rpkv4y2gwfl">™</sup>
          </span>
          <span className="sr-only" data-id="element-296" data-mp="32tk3gclv8cgi"> · </span>
          <span className="mt-0.5 block font-heading text-[1.35rem] font-bold text-orange" data-id="element-297" data-mp="3gqgx0ltdc813">{PRICE.label}</span>
        </p>
        <CheckoutButton label="GET IT NOW" size="sm" className="shrink-0" tabIndex={visible ? 0 : -1} data-id="element-298" data-mp="3btgrqd4cf9b1" />
      </div>
    </div>;
}